import { useGetSkillsQuery } from '$store/modules/form-info';
import { Skill } from '$types/candidates';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from 'primereact/autocomplete';
import { Divider } from 'primereact/divider';
import { InputTextarea } from 'primereact/inputtextarea';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import SPKToast from '$components/SPKToast/SPKToast';
import { ReactComponent as CircledCheckmark } from '$icons/uncolored/circled_checkmark.svg';
import { ReactComponent as Close } from '$icons/uncolored/Close.svg';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { companySlice } from '$store/modules/companies';
import { SeniorityLevelType } from '$types/position/positionPropertyValues';
import EducationDegreeTypes from '$types/DegreeTypes';

const SPKJobDescriptionStep = ({
  onComplete,
  onGoBack,
}: {
  onComplete: () => void;
  onGoBack: () => void;
}) => {
  const MAX_SIZE = 1024 * 1024 * 50; // 50 MB
  const [t] = useTranslation('global');

  const [requiredSkillsSuggestionsList, setRequiredSkillsSuggestionsList] =
    useState<any>([]);
  const [niceToHaveSkillsSuggestionsList, setNiceToHaveSkillsSuggestionsList] =
    useState<any>([]);

  const dispatch = useAppDispatch();
  const { employerAddPositionForm } = useAppSelector((state) => state.company);
  const {
    file,
    RequiredSkills,
    NiceToHaveSkills,
    Responsibilities,
    SeniorityLevel,
    JobDescription,
    DegreeType,
  } = employerAddPositionForm;

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      file,
      RequiredSkills,
      NiceToHaveSkills,
      Responsibilities,
      SeniorityLevel,
      JobDescription,
      DegreeType,
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        [],
    },
    maxSize: MAX_SIZE,
    maxFiles: 1,
    onDrop: (e: File[], onDropError) => {
      if (
        onDropError &&
        onDropError.length > 0 &&
        onDropError[0].errors[0].code === 'file-invalid-type'
      ) {
        SPKToast(t('EmployerAddPosition.Errors.InvalidFileType'), {
          type: 'Error',
        });
      } else setValue('file', e[0]);
    },
  });
  const selectedRequiredSkills = watch('RequiredSkills');
  const selectedNiceToHaveSkills = watch('NiceToHaveSkills');
  const selectedFile = watch('file');

  const { data: skillsData } = useGetSkillsQuery({
    t,
  });

  const autoCompleteRequiredSkillsFilter = ({
    query,
  }: AutoCompleteCompleteEvent) => {
    if (!skillsData) return;
    const tempSkillsData = [...skillsData]
      .filter((singleSkill) => {
        const isNotSelected = !(selectedRequiredSkills as Skill[]).find(
          (singleReqSkill) => singleReqSkill.Id === singleSkill.Id
        );

        const containsQuery =
          singleSkill.title.toLowerCase().indexOf(query.toLowerCase().trim()) >=
          0;

        const isNotANiceToHaveSkill = !(
          selectedNiceToHaveSkills as Skill[]
        ).find((singleReqSkill) => singleReqSkill.Id === singleSkill.Id);

        return isNotSelected && containsQuery && isNotANiceToHaveSkill;
      })
      .sort((a, b) => a.title.localeCompare(b.title));
    setRequiredSkillsSuggestionsList(tempSkillsData);
  };

  const autoCompleteNiceToHaveSkillsFilter = ({
    query,
  }: AutoCompleteCompleteEvent) => {
    if (!skillsData) return;
    const tempSkillsData = [...skillsData]
      .filter((singleSkill) => {
        const isNotSelected = !(selectedNiceToHaveSkills as Skill[]).find(
          (singleReqSkill) => singleReqSkill.Id === singleSkill.Id
        );

        const containsQuery =
          singleSkill.title.toLowerCase().indexOf(query.toLowerCase().trim()) >=
          0;

        const isNotARequiredSkill = !(selectedRequiredSkills as Skill[]).find(
          (singleReqSkill) => singleReqSkill.Id === singleSkill.Id
        );

        return isNotSelected && containsQuery && isNotARequiredSkill;
      })
      .sort((a, b) => a.title.localeCompare(b.title));
    setNiceToHaveSkillsSuggestionsList(tempSkillsData);
  };

  const saveDataIntoAppState = () => {
    const {
      file: tempFile,
      RequiredSkills: tempRequiredSkills,
      NiceToHaveSkills: tempNiceToHaveSkills,
      Responsibilities: tempResponsibilities,
      SeniorityLevel: tempSeniority,
      JobDescription: tempJobDescription,
      DegreeType: tempDegreeType,
    } = watch();
    dispatch(
      companySlice.actions.employerAddPositionOverview({
        ...employerAddPositionForm,
        file: tempFile,
        RequiredSkills: tempRequiredSkills,
        NiceToHaveSkills: tempNiceToHaveSkills,
        Responsibilities: tempResponsibilities,
        SeniorityLevel: tempSeniority,
        JobDescription: tempJobDescription,
        DegreeType: tempDegreeType,
      })
    );
  };

  const successSubmitHandler = () => {
    saveDataIntoAppState();
    onComplete();
  };

  const goBackHandler = () => {
    saveDataIntoAppState();
    onGoBack();
  };

  const errorSubmitHandler = () => {
    SPKToast(t('EmployerAddPosition.Errors.MissingRequiredFields'), {
      type: 'Error',
    });
  };

  return (
    <>
      <h1 className="text-4xl font-extrabold mb-8">
        {t('EmployerAddPosition.JobDescription')}
      </h1>
      <form
        onSubmit={handleSubmit(successSubmitHandler, errorSubmitHandler)}
        className="text-spk-mediumContrast text-xs w-full flex flex-col"
      >
        {/* File Upload */}
        <div className="flex flex-col gap-2">
          <label htmlFor="file">
            {t('EmployerAddPosition.JobDescription')}
          </label>
          <Controller
            control={control}
            name="file"
            render={({ field }) => (
              <div>
                <div
                  className={`border border-solid border-employer-secondary bg-spk-neutral-30 rounded-2xl p-8 text-center text-base ${
                    isDragActive && 'bg-employer-secondary'
                  }`}
                  {...getRootProps()}
                >
                  {!selectedFile ? (
                    <>
                      <i className="pi pi-cloud-upload text-employer-primary text-3xl" />
                      <h1 className="font-bold">
                        {t('EmployerAddPosition.DragOrDropFile')}
                      </h1>
                      <h6>{t('EmployerAddPosition.DragAndDropValidFiles')}</h6>
                    </>
                  ) : (
                    <h1 className="flex flex-col gap-2 items-center justify-center text-white">
                      <CircledCheckmark className="fill-spk-highContrast mb-2" />
                      <div className="relative">
                        <span className="bg-spk-highContrast px-4 py-2 rounded-md text-sm flex flex-row">
                          <span className="max-w-96 truncate overflow-hidden inline-block -mr-1">
                            {(selectedFile as File).name.split('.')[0]}
                          </span>
                          <span>
                            {(selectedFile as File).name.split('.')[1]}
                          </span>
                        </span>
                        <button
                          type="button"
                          aria-label="Remove file"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setValue('file', null);
                          }}
                          className="absolute rounded-full bg-employer-secondary -top-4 -right-4"
                        >
                          <Close className="w-6 h-6 stroke-spk-highContrast p-1" />
                        </button>
                      </div>
                    </h1>
                  )}
                </div>
                <input
                  {...getInputProps({
                    onChange: (e) => {
                      const userFile = e.target.files?.[0];
                      field.onChange(userFile);
                    },
                  })}
                />
              </div>
            )}
          />
        </div>

        <Divider align="center">
          <span className="font-normal">
            {t('EmployerAddPosition.OrManualAdd')}
          </span>
        </Divider>

        <fieldset className="gap-8 flex flex-col">
          {/* Required skills */}
          <div className="flex flex-col gap-2">
            <label htmlFor="RequiredSkills">
              {t('EmployerAddPosition.RequiredSkills')}
            </label>
            <Controller
              control={control}
              name="RequiredSkills"
              rules={{
                validate: (value) => {
                  return (
                    !!selectedFile ||
                    value.length > 0 ||
                    t('EmployerAddPosition.Errors.AtLeastOneSkill')
                  );
                },
              }}
              render={({ field }) => (
                <AutoComplete
                  {...field}
                  className="w-full"
                  field="title"
                  placeholder={t('EmployerAddPosition.Placeholders.TypeASkill')}
                  suggestions={requiredSkillsSuggestionsList}
                  completeMethod={autoCompleteRequiredSkillsFilter}
                  virtualScrollerOptions={{
                    itemSize: 38,
                    scrollHeight: `${
                      Math.min(requiredSkillsSuggestionsList?.length || 0, 5) *
                      (requiredSkillsSuggestionsList.length > 2 ? 45 : 50)
                    }px`,
                  }}
                  dropdown
                  multiple
                  forceSelection
                  pt={{
                    container: {
                      className: `w-full ${
                        errors.RequiredSkills?.message &&
                        'border-spk-brightRed outline-none focus-visible:outline-none focus-within:outline-none focus-within:shadow-none'
                      }`,
                    },
                    input: {
                      // @ts-ignore
                      className: `outline-none`,
                    },
                    dropdownButton: {
                      root: { className: 'hidden' },
                    },
                  }}
                />
              )}
            />
            {errors.RequiredSkills?.message && (
              <span className="text-xs text-spk-brightRed">
                *{errors.RequiredSkills.message}
              </span>
            )}
          </div>
          {/* Nice to have skills */}
          <div className="flex flex-col gap-2">
            <label htmlFor="NiceToHaveSkills">
              {t('EmployerAddPosition.NiceToHaveSkills')}
            </label>
            <Controller
              control={control}
              name="NiceToHaveSkills"
              // rules={{
              //   validate: (value) =>
              //     !!selectedFile ||
              //     value.length !== 0 ||
              //     t('EmployerAddPosition.Errors.AtLeastOneSkill'),
              // }}
              render={({ field }) => (
                <AutoComplete
                  {...field}
                  className="w-full"
                  field="title"
                  placeholder={t('EmployerAddPosition.Placeholders.TypeASkill')}
                  suggestions={niceToHaveSkillsSuggestionsList}
                  completeMethod={autoCompleteNiceToHaveSkillsFilter}
                  virtualScrollerOptions={{
                    itemSize: 38,
                    scrollHeight: `${
                      Math.min(
                        niceToHaveSkillsSuggestionsList?.length || 0,
                        5
                      ) * (niceToHaveSkillsSuggestionsList.length > 2 ? 45 : 50)
                    }px`,
                  }}
                  dropdown
                  multiple
                  forceSelection
                  pt={{
                    container: {
                      className: `w-full ${
                        errors.NiceToHaveSkills?.message &&
                        'border-spk-brightRed outline-none focus-visible:outline-none focus-within:outline-none focus-within:shadow-none'
                      }`,
                    },
                    input: {
                      // @ts-ignore
                      className: `outline-none`,
                    },
                    dropdownButton: {
                      root: { className: 'hidden' },
                    },
                  }}
                />
              )}
            />
            {errors.NiceToHaveSkills?.message && (
              <span className="text-xs text-spk-brightRed">
                *{errors.NiceToHaveSkills.message}
              </span>
            )}
          </div>
          {/* Responsabilities */}
          <div className="flex flex-col gap-2">
            <label htmlFor="Responsibilities">
              {t('EmployerAddPosition.Responsibilities')}
            </label>
            <Controller
              control={control}
              name="Responsibilities"
              // rules={{
              //   required: selectedFile
              //     ? false
              //     : t('EmployerAddPosition.Errors.RequiredField'),
              // }}
              render={({ field }) => (
                <InputTextarea
                  autoResize
                  placeholder={t(
                    'EmployerAddPosition.Placeholders.KeyResponsabilities'
                  )}
                  className={`min-h-32 ${
                    errors.Responsibilities?.message &&
                    'border-spk-brightRed focus-within:shadow-none'
                  }`}
                  {...field}
                />
              )}
            />
            {errors.Responsibilities?.message && (
              <span className="text-xs text-spk-brightRed">
                *{errors.Responsibilities?.message}
              </span>
            )}
          </div>
          {/* Job Objectives */}
          <div className="flex flex-col gap-2">
            <label htmlFor="JobDescription">
              {t('EmployerAddPosition.PositionObjective')}
            </label>
            <Controller
              control={control}
              name="JobDescription"
              render={({ field }) => (
                <InputTextarea
                  autoResize
                  placeholder={t(
                    'EmployerAddPosition.Placeholders.JobDescription'
                  )}
                  className="min-h-32"
                  {...field}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="SeniorityLevel">
              {t('EmployerAddPosition.Seniority')}
            </label>
            <Controller
              control={control}
              name="SeniorityLevel"
              rules={{
                required: selectedFile
                  ? false
                  : t('EmployerAddPosition.Errors.RequiredField'),
              }}
              render={({ field }) => (
                <SelectButton
                  options={
                    Object.keys(SeniorityLevelType).map(
                      (singleSeniority) =>
                        SeniorityLevelType[
                          singleSeniority as keyof typeof SeniorityLevelType
                        ]
                    ) as string[]
                  }
                  {...field}
                  allowEmpty={false}
                  pt={{
                    button: {
                      className: `${
                        errors.SeniorityLevel?.message && 'border-spk-brightRed'
                      }`,
                    },
                  }}
                />
              )}
            />
            {errors.SeniorityLevel?.message && (
              <span className="text-xs text-spk-brightRed">
                *{errors.SeniorityLevel?.message}
              </span>
            )}
          </div>
          {/* Degree Type */}
          <div className="flex flex-col gap-2">
            <label htmlFor="DegreeType">
              {t('EmployerAddPosition.PositionDegreeType')}
            </label>
            <Controller
              control={control}
              name="DegreeType"
              render={({ field }) => (
                <Dropdown
                  {...field}
                  id="DegreeType"
                  options={Object.values(EducationDegreeTypes)}
                  placeholder={t(
                    'EmployerAddPosition.Placeholders.SelectAnOption'
                  )}
                  showClear
                  className="w-full"
                />
              )}
            />
          </div>
        </fieldset>

        <div className="flex flex-row gap-8 w-full justify-center mt-8">
          <button
            type="button"
            onClick={() => goBackHandler()}
            className="text-base text-employer-primary font-semibold"
          >
            {t('EmployerAddPosition.GoBack')}
          </button>
          <button
            type="submit"
            className="rounded-full bg-employer-primary px-4 py-2 !text-spk-softBlue text-base font-semibold"
          >
            {t('EmployerAddPosition.ConfirmAndContinue')}
          </button>
        </div>
      </form>
    </>
  );
};

export default SPKJobDescriptionStep;
